import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAdminInventory } from "@/lib/inventory/admin-client";
import { adjustSizeStockFn, setSizeStockFn } from "@/lib/inventory/inventory.functions";
import { BOTTLE_SIZES, LOW_STOCK_THRESHOLD, sizeLabel, stockTone } from "@/lib/inventory/sizes";
import { logAction } from "@/lib/admin/audit/log";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";

export const Route = createFileRoute("/admin/inventory")({ component: InventoryPage });

function InventoryPage() {
  const { rows, loading, error, reload } = useAdminInventory();
  const [q, setQ] = useState("");
  const [only, setOnly] = useState<"all" | "low" | "out">("all");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((r) => {
      const quantities = BOTTLE_SIZES.map((s) => r.sizes[String(s)] ?? 0);
      if (only === "low" && !quantities.some((v, i) => v > 0 && v <= (r.thresholds[String(BOTTLE_SIZES[i])] ?? LOW_STOCK_THRESHOLD))) return false;
      if (only === "out" && !quantities.some((v) => v === 0)) return false;
      if (t && !(r.name.toLowerCase().includes(t) || r.slug.toLowerCase().includes(t))) return false;
      return true;
    });
  }, [rows, q, only]);

  // Low / out are evaluated independently per bottle size.
  let total = 0;
  let low = 0;
  let out = 0;
  for (const r of rows) {
    for (const s of BOTTLE_SIZES) {
      const qty = r.sizes[String(s)] ?? 0;
      total += qty;
      const tone = stockTone(qty, r.thresholds[String(s)] ?? LOW_STOCK_THRESHOLD);
      if (tone === "out") out += 1;
      else if (tone === "low") low += 1;
    }
  }

  const adjust = async (productId: string, name: string, sizeMl: number, delta: number, current: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await adjustSizeStockFn({ data: { productId, sizeMl, delta } });
      logAction({
        entity: "inventory",
        entityId: `${productId}:${sizeMl}`,
        action: "adjust",
        summary: `${name} ${sizeLabel(sizeMl)}: ${current} to ${Math.max(0, current + delta)}`,
        before: current,
        after: Math.max(0, current + delta),
      });
      reload();
    } finally {
      setBusy(false);
    }
  };

  const setExact = async (productId: string, name: string, sizeMl: number, value: number, current: number) => {
    await setSizeStockFn({ data: { productId, sizeMl, onHand: value } });
    logAction({
      entity: "inventory",
      entityId: `${productId}:${sizeMl}`,
      action: "set",
      summary: `${name} ${sizeLabel(sizeMl)}: ${current} to ${value}`,
      before: current,
      after: value,
    });
    reload();
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Inventory" subtitle="Stock levels per bottle size across all catalog products" />
      <div className="grid grid-cols-3 gap-3">
        <Metric label="Total units" value={total.toString()} />
        <Metric label="Low stock (sizes)" value={low.toString()} tone="warn" />
        <Metric label="Out of stock (sizes)" value={out.toString()} tone="danger" />
      </div>
      <div className="flex flex-wrap items-center gap-2 border border-black/10 bg-white p-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="flex-1 min-w-[200px] border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/50"/>
        <select value={only} onChange={(e) => setOnly(e.target.value as typeof only)} className="border border-black/15 px-2 py-2 text-sm">
          <option value="all">All items</option>
          <option value="low">Low stock</option>
          <option value="out">Out of stock</option>
        </select>
      </div>
      {error && <p className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">Could not load inventory: {error}</p>}
      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Size</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">On hand</th>
              <th className="px-3 py-2 text-right">Adjust</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-black/50">Loading...</td></tr>}
            {!loading && filtered.map((r) =>
              BOTTLE_SIZES.map((s, i) => {
                const qty = r.sizes[String(s)] ?? 0;
                const threshold = r.thresholds[String(s)] ?? LOW_STOCK_THRESHOLD;
                const tone = stockTone(qty, threshold);
                return (
                  <tr key={`${r.productId}:${s}`} className={i === BOTTLE_SIZES.length - 1 ? "border-b border-black/10" : "border-b border-black/5"}>
                    <td className="px-3 py-2 font-medium text-black">{i === 0 ? r.name : ""}</td>
                    <td className="px-3 py-2 text-black/60">{sizeLabel(s)}</td>
                    <td className="px-3 py-2">
                      {tone === "out" ? <StatusPill tone="danger">Out</StatusPill>
                        : tone === "low" ? <StatusPill tone="warn">Low</StatusPill>
                        : <StatusPill tone="success">In stock</StatusPill>}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={qty}
                        onChange={(e) => void setExact(r.productId, r.name, s, Math.max(0, Number(e.target.value)), qty)}
                        className="w-20 border border-black/15 px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => void adjust(r.productId, r.name, s, -1, qty)} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">-1</button>
                        <button onClick={() => void adjust(r.productId, r.name, s, +1, qty)} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">+1</button>
                        <button onClick={() => void adjust(r.productId, r.name, s, +10, qty)} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">+10</button>
                      </div>
                    </td>
                  </tr>
                );
              }),
            )}
            {!loading && filtered.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-black/50">No products match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "warn" | "danger" }) {
  const color = tone === "danger" ? "text-red-700" : tone === "warn" ? "text-amber-700" : "text-black";
  return (
    <div className="border border-black/10 bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tracking-tight ${color}`}>{value}</p>
    </div>
  );
}
